import React, { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  FaBoxOpen,
  FaGlobeAmericas,
  FaInfoCircle,
  FaSearch,
  FaTag,
} from "react-icons/fa";
import { useSearchTremendousProductsMutation } from "../../tremendous.service";
import {
  TREMENDOUS_PRODUCT_CATEGORIES,
  TremendousProduct,
} from "../../types/tremendous.entity";
import { CreateRewardFormData } from "../../utils/reward.validation";
import { countryOptions } from "src/constants/countries";
import {
  getTremendousCurrencyOptions,
  getTremendousSkuBounds,
} from "../../utils/tremendous";

const formatLabel = (value: string) =>
  value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const normalizeOptionalText = (value?: string | null): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

const normalizeImageUrl = (value?: string | null): string | undefined => {
  if (typeof value !== "string" || !value.trim()) return undefined;

  try {
    new URL(value);
    return value;
  } catch {
    return undefined;
  }
};

const normalizeTremendousProduct = (product: TremendousProduct): TremendousProduct => ({
  ...product,
  description: normalizeOptionalText(product.description),
  subcategory: normalizeOptionalText(product.subcategory),
  disclosure: normalizeOptionalText(product.disclosure),
  countries: Array.isArray(product.countries)
    ? product.countries.filter((country): country is string => typeof country === "string" && !!country.trim())
    : [],
  currencyCodes: Array.isArray(product.currencyCodes)
    ? product.currencyCodes.filter((currency): currency is string => typeof currency === "string" && !!currency.trim())
    : undefined,
  skus: Array.isArray(product.skus)
    ? product.skus
        .filter(
          (sku) =>
            sku &&
            typeof sku.min === "number" &&
            typeof sku.max === "number" &&
            Array.isArray(sku.currencyCodes)
        )
        .map((sku) => ({
          min: sku.min,
          max: sku.max,
          currencyCodes: sku.currencyCodes.filter(
            (currency): currency is string => typeof currency === "string" && !!currency.trim()
          ),
        }))
    : [],
  imageUrl: normalizeImageUrl(product.imageUrl),
});

export const TremendousProductSelector: React.FC = () => {
  const {
    watch,
    setValue,
    trigger,
    register,
    getValues,
    formState: { errors, dirtyFields },
  } = useFormContext<CreateRewardFormData>();

  const [searchProducts, { data, isLoading, error }] = useSearchTremendousProductsMutation();
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [countryFilter, setCountryFilter] = useState("ALL");

  const selectedProduct = watch("tremendousProduct");
  const selectedCurrencyCode = watch("tremendousCurrencyCode");
  const currencyField = register("tremendousCurrencyCode");
  const products = data?.products || [];
  const displayedProducts = useMemo(() => {
    if (countryFilter === "ALL") return products;
    return products.filter((product) => product.countries.includes(countryFilter));
  }, [countryFilter, products]);

  const currencyOptions = useMemo(
    () => getTremendousCurrencyOptions(selectedProduct),
    [selectedProduct]
  );

  const selectedBounds = useMemo(
    () => getTremendousSkuBounds(selectedProduct, selectedCurrencyCode),
    [selectedCurrencyCode, selectedProduct]
  );

  useEffect(() => {
    setValue("category", "DIGITAL", { shouldDirty: true });
  }, [setValue]);

  useEffect(() => {
    if (!selectedProduct) return;

    if (!currencyOptions.length) {
      setValue("tremendousCurrencyCode", "", { shouldDirty: true });
      return;
    }

    if (!selectedCurrencyCode || !currencyOptions.includes(selectedCurrencyCode)) {
      const nextCurrency = currencyOptions[0];
      const nextBounds = getTremendousSkuBounds(selectedProduct, nextCurrency);

      setValue("tremendousCurrencyCode", nextCurrency, { shouldDirty: true });
      setValue("tremendousDenomination", nextBounds?.min || 0, { shouldDirty: true });
      return;
    }
  }, [
    currencyOptions,
    selectedCurrencyCode,
    selectedProduct,
    setValue,
  ]);

  const handleSearch = async () => {
    await searchProducts({
      ...(query.trim() ? { query: query.trim() } : {}),
      ...(categoryFilter !== "ALL" ? { category: categoryFilter } : {}),
      ...(countryFilter !== "ALL" ? { country: countryFilter } : {}),
      limit: 24,
    });
  };

  const handleSelectProduct = (product: TremendousProduct) => {
    const normalizedProduct = normalizeTremendousProduct(product);
    const nextCurrency = getTremendousCurrencyOptions(normalizedProduct)[0] || "";
    const nextBounds = getTremendousSkuBounds(normalizedProduct, nextCurrency);
    const currentName = getValues("name").trim();
    const currentDescription = getValues("description").trim();
    const shouldAutofillName = !dirtyFields.name || !currentName;
    const shouldAutofillDescription = !dirtyFields.description || !currentDescription;

    setValue("providerName", "TREMENDOUS", { shouldDirty: true, shouldTouch: true });
    setValue("category", "DIGITAL", { shouldDirty: true });
    setValue("tremendousProduct", normalizedProduct, { shouldDirty: true, shouldTouch: true });
    setValue("tremendousCurrencyCode", nextCurrency, { shouldDirty: true });
    setValue("tremendousDenomination", nextBounds?.min || 0, { shouldDirty: true });
    setValue("countryList", normalizedProduct.countries, { shouldDirty: true });

    if (shouldAutofillName) {
      setValue("name", normalizedProduct.name, { shouldDirty: true });
    }

    if (shouldAutofillDescription) {
      setValue("description", normalizedProduct.description || normalizedProduct.name, { shouldDirty: true });
    }

    const fieldsToTrigger: Array<keyof CreateRewardFormData> = [
      "tremendousProduct",
      "tremendousCurrencyCode",
      "tremendousDenomination",
      "countryList",
    ];

    if (shouldAutofillName) {
      fieldsToTrigger.push("name");
    }

    if (shouldAutofillDescription) {
      fieldsToTrigger.push("description");
    }

    trigger(fieldsToTrigger);
  };

  const errorMessage =
    (error as { message?: string } | undefined)?.message ||
    "We couldn't load Tremendous products right now.";

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-fuchsia-100 bg-gradient-to-br from-fuchsia-50 via-white to-pink-50 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-fuchsia-100 p-3 text-fuchsia-600">
            <FaInfoCircle className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">Tremendous product lookup</p>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              Search the live Tremendous catalog through a secure Cloud Function, then save the selected
              product mapping into this reward.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_180px_180px_auto]">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSearch();
                }
              }}
              placeholder="Search Tremendous products, for example Google Play"
              className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-10 pr-4 text-sm focus:border-fuchsia-400 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
            />
          </div>

          <select
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            className="rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm focus:border-fuchsia-400 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
          >
            <option value="ALL">All categories</option>
            {TREMENDOUS_PRODUCT_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {formatLabel(category)}
              </option>
            ))}
          </select>

          <select
            value={countryFilter}
            onChange={(event) => setCountryFilter(event.target.value)}
            className="rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm focus:border-fuchsia-400 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
          >
            <option value="ALL">All countries</option>
            {countryOptions.map((country) => (
              <option key={country.value} value={country.value}>
                {country.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={handleSearch}
            disabled={isLoading}
            className={`rounded-xl px-5 py-3 text-sm font-semibold text-white transition-colors ${
              isLoading ? "bg-fuchsia-300 cursor-not-allowed" : "bg-fuchsia-600 hover:bg-fuchsia-700"
            }`}
          >
            {isLoading ? "Searching..." : "Search"}
          </button>
        </div>
      </div>

      {selectedProduct ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            <div className="h-24 w-24 overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-sm">
              {selectedProduct.imageUrl ? (
                <img
                  src={selectedProduct.imageUrl}
                  alt={selectedProduct.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-fuchsia-500 to-pink-500 text-white">
                  <FaBoxOpen className="h-8 w-8" />
                </div>
              )}
            </div>

            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
                Selected Tremendous product
              </p>
              <h4 className="mt-2 text-lg font-bold text-gray-900">{selectedProduct.name}</h4>
              <p className="mt-2 text-sm leading-6 text-gray-600">
                {selectedProduct.description || "No Tremendous description was returned for this product."}
              </p>
              <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold text-gray-600">
                <span className="rounded-full bg-white px-3 py-1 ring-1 ring-emerald-100">
                  {formatLabel(selectedProduct.category)}
                </span>
                {selectedProduct.subcategory && (
                  <span className="rounded-full bg-white px-3 py-1 ring-1 ring-emerald-100">
                    {formatLabel(selectedProduct.subcategory)}
                  </span>
                )}
                <span className="rounded-full bg-white px-3 py-1 ring-1 ring-emerald-100">
                  {selectedProduct.countries.length} countries
                </span>
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Currency
              </label>
              <select
                {...currencyField}
                onChange={(event) => {
                  currencyField.onChange(event);

                  const nextCurrency = event.target.value;
                  const nextBounds = getTremendousSkuBounds(selectedProduct, nextCurrency);

                  setValue("tremendousDenomination", nextBounds?.min || 0, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm focus:border-fuchsia-400 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
              >
                {currencyOptions.map((currencyCode) => (
                  <option key={currencyCode} value={currencyCode}>
                    {currencyCode}
                  </option>
                ))}
              </select>
              {errors.tremendousCurrencyCode ? (
                <p className="text-xs text-red-500">{errors.tremendousCurrencyCode.message}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Denomination
              </label>
              <input
                type="number"
                min={selectedBounds?.min || 0}
                max={selectedBounds?.max}
                step="0.01"
                {...register("tremendousDenomination")}
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm focus:border-fuchsia-400 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
              />
              {selectedBounds ? (
                <p className="text-xs text-gray-500">
                  Allowed range: {selectedCurrencyCode} {selectedBounds.min} - {selectedBounds.max}
                </p>
              ) : null}
              {errors.tremendousDenomination ? (
                <p className="text-xs text-red-500">{errors.tremendousDenomination.message}</p>
              ) : null}
            </div>
          </div>

          {selectedProduct.disclosure && (
            <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
              <span className="font-semibold">Disclosure:</span> {selectedProduct.disclosure}
            </div>
          )}
        </div>
      ) : null}

      {errors.tremendousProduct ? (
        <p className="text-xs text-red-500">{errors.tremendousProduct.message}</p>
      ) : null}

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      {products.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">Search results</p>
            <p className="text-xs text-gray-500">{displayedProducts.length} products returned</p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {displayedProducts.map((product) => {
              const productCurrencies = getTremendousCurrencyOptions(product);
              const isSelected = selectedProduct?.id === product.id;

              return (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => handleSelectProduct(product)}
                  className={`rounded-2xl border p-4 text-left transition-all ${
                    isSelected
                      ? "border-fuchsia-400 bg-fuchsia-50 shadow-sm ring-1 ring-fuchsia-300"
                      : "border-gray-200 bg-white hover:border-fuchsia-200 hover:shadow-sm"
                  }`}
                >
                  <div className="flex gap-4">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-fuchsia-500 to-pink-500 text-white">
                          <FaBoxOpen className="h-5 w-5" />
                        </div>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="truncate text-sm font-bold text-gray-900">{product.name}</h4>
                        <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-semibold text-gray-600">
                          {formatLabel(product.category)}
                        </span>
                      </div>

                      <p className="mt-2 line-clamp-2 text-sm leading-6 text-gray-500">
                        {product.description || "No description provided by Tremendous."}
                      </p>

                      <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold text-gray-500">
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2.5 py-1 ring-1 ring-gray-200">
                          <FaTag className="h-3 w-3" />
                          {productCurrencies.join(", ") || "No currencies"}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2.5 py-1 ring-1 ring-gray-200">
                          <FaGlobeAmericas className="h-3 w-3" />
                          {product.countries.length} countries
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {products.length > 0 && displayedProducts.length === 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          No Tremendous products matched the selected country.
        </div>
      ) : null}
    </div>
  );
};
